/**
 * Voice parse — Zod-validated contract between the frontend and
 * `POST /api/ai/transcribe-voice`.
 *
 * WHY a dedicated module (not just an `axios.post`):
 *  - Every voice-driven surface (Capture today, Assistant chat tomorrow) needs
 *    the SAME parsed shape. Owning the schema here means a backend field-drift
 *    is caught at the seam with a Zod error instead of a silent `undefined`
 *    render further downstream.
 *  - Zod acts as the runtime disclaimer: if Claude ever hallucinates a
 *    structure we don't expect, we FAIL LOUD with `.safeParse()` and fall
 *    back to a conservative shape rather than displaying garbage.
 *  - The `PersonalSuggestion` type is preserved verbatim so the existing
 *    `PersonalExpenseToggle` in Review keeps working with zero changes.
 */

import axios, { AxiosProgressEvent } from "axios";
import { z } from "zod";
import { api } from "@/lib/api";

/* ------------------------------------------------------------------ */
/* Sub-schemas                                                        */
/* ------------------------------------------------------------------ */

const ConfidenceSchema = z.object({
  supplier: z.number().min(0).max(100).optional(),
  total: z.number().min(0).max(100).optional(),
  gst: z.number().min(0).max(100).optional(),
  date: z.number().min(0).max(100).optional(),
  overall: z.number().min(0).max(100).optional(),
}).passthrough();

/**
 * `personal_suggestion` matches the shape emitted by the backend
 * `classify_personal_suggestion` heuristic. Kept forgiving (all fields
 * optional) so a partial classifier response never nukes the parse.
 */
const PersonalSuggestionSchema = z.object({
  top_kind: z.string().optional(),
  sub_category: z.string().optional(),
  reason: z.string().optional(),
}).passthrough();

/* ------------------------------------------------------------------ */
/* Full response schema                                                */
/* ------------------------------------------------------------------ */

export const VoiceParseResultSchema = z.object({
  supplier: z.string().default(""),
  description: z.string().default(""),
  total: z.number().default(0),
  gst: z.number().default(0),
  entry_date: z.string().default(""),
  deduction_category: z.string().default("materials"),
  notes: z.string().default(""),
  confirmation_line: z.string().default(""),
  confidence: ConfidenceSchema.default({}),
  raw_transcript: z.string().default(""),
  source: z.string().default("voice"),
  voice_path: z.string().nullable().optional(),
  voice_size: z.number().optional(),
  personal_suggestion: PersonalSuggestionSchema.optional(),
  // Forward-compat: anything else the backend adds (twin insights, job_id
  // hint, etc.) is preserved untouched — Review still consumes them.
}).passthrough();

export type VoiceParseResult = z.infer<typeof VoiceParseResultSchema>;

/* ------------------------------------------------------------------ */
/* API client                                                          */
/* ------------------------------------------------------------------ */

export interface TranscribeVoiceOptions {
  /** Filename hint passed as the multipart part filename. Defaults to voice.webm. */
  filename?: string;
  /** Upload progress callback (0-100). */
  onProgress?: (percent: number) => void;
  /** AbortSignal for cancellation. */
  signal?: AbortSignal;
}

/**
 * Upload a recorded audio Blob to the transcribe-voice endpoint.
 * Returns a Zod-validated, forward-compatible parse result.
 *
 * Throws on network / server error — caller decides whether to fall back
 * to a manual review flow.
 */
export async function transcribeVoice(
  blob: Blob,
  options: TranscribeVoiceOptions = {}
): Promise<VoiceParseResult> {
  const { filename = "voice.webm", onProgress, signal } = options;
  const fd = new FormData();
  fd.append("file", blob, filename);
  const { data } = await api.post("/ai/transcribe-voice", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
    onUploadProgress: (evt: AxiosProgressEvent) => {
      if (!onProgress || !evt.total) return;
      onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  // `.parse()` is safe here: unknown fields flow through untouched thanks to
  // `.passthrough()`, defaults fill in gaps, and hard type mismatches (which
  // should never happen with our own backend) surface as a loud dev error.
  return VoiceParseResultSchema.parse(data);
}

// Re-export axios helper for consumers that want to compose with their own
// interceptors (e.g. a future assistant chat that shares this axios instance).
export { axios as _axios };
